import React, { type PropsWithChildren } from 'react'
import { Flex, Modal, type ModalProps } from 'antd'

// info is for things when the user should double check what they are doing
// warning is when they might do something dangerous
export type ConfirmModalType = 'info' | 'warning'

interface ConfirmModalProps extends ModalProps {
    type: ConfirmModalType
    cancelButtonText?: React.ReactNode
    confirmButtonText?: React.ReactNode
    onConfirmClicked: () => void
    isSubmitButtonDisabled?: boolean
    // It is typical for the mobile version to use vertical buttons and desktop to be horizontal. This makes it a little
    // tricky to make it responsive as the layout is actually different in the html.
    buttonOrientation?: 'horizontal' | 'vertical'
    buttonSize?: 'large' | 'default' | 'small' | 'auto'
    // when tagging the elements for use with things like Pendo this will be used as a prefix
    // to make sure the ids are unique - it doesn't really matter what it is, but it should be unique and static
    dataIdPrefix?: string
}

export function ConfirmModal(props: PropsWithChildren<ConfirmModalProps>) {
    return (
        <>
            <Modal
                className={props.className ? ' ' + props.className : ''}
                data-testid="confirm-modal"
                onCancel={props.onCancel}
                centered={true}
                footer={() => (
                    <Flex gap={'8px'} vertical={props.buttonOrientation === 'vertical'}>
                        {props.buttonOrientation === 'vertical' ? (
                            <>
                                <PrimaryButton
                                    onConfirmClicked={props.onConfirmClicked}
                                    confirmButtonText={props.confirmButtonText}
                                    isSubmitButtonDisabled={props.isSubmitButtonDisabled}
                                    type={props.type}
                                    size={props.buttonSize || 'default'}
                                    data-insight={props.dataIdPrefix ? `${props.dataIdPrefix}-confirm-button` : null}
                                />
                                <SecondaryButton
                                    onCancel={props.onCancel}
                                    cancelButtonText={props.cancelButtonText}
                                    size={props.buttonSize || 'default'}
                                    data-insight={props.dataIdPrefix ? `${props.dataIdPrefix}-cancel-button` : null}
                                />
                            </>
                        ) : (
                            <>
                                <SecondaryButton
                                    onCancel={props.onCancel}
                                    cancelButtonText={props.cancelButtonText}
                                    size={props.buttonSize || 'default'}
                                    dataInsight={props.dataIdPrefix ? `${props.dataIdPrefix}-cancel-button` : null}
                                />
                                <PrimaryButton
                                    onConfirmClicked={props.onConfirmClicked}
                                    confirmButtonText={props.confirmButtonText}
                                    isSubmitButtonDisabled={props.isSubmitButtonDisabled}
                                    type={props.type}
                                    size={props.buttonSize || 'default'}
                                    dataInsight={props.dataIdPrefix ? `${props.dataIdPrefix}-confirm-button` : null}
                                />
                            </>
                        )}
                    </Flex>
                )}
                {...props}
            >
                {props.children}
            </Modal>
        </>
    )
}

function SecondaryButton(props: {
    onCancel: (e: React.MouseEvent<HTMLButtonElement>) => void
    cancelButtonText?: React.ReactNode
    size?: 'large' | 'default' | 'small' | 'auto'
    dataInsight?: string | null
}) {
    return (
        <zaptic-button
            size={props.size || 'auto'}
            type="secondary"
            onClick={props.onCancel}
            data-insight={props.dataInsight}
        >
            {props.cancelButtonText || window.i18n('appcore:common.cancel')}
        </zaptic-button>
    )
}

function PrimaryButton(props: {
    onConfirmClicked: () => void
    confirmButtonText?: React.ReactNode
    isSubmitButtonDisabled?: boolean
    type: ConfirmModalType
    size?: 'large' | 'default' | 'small' | 'auto'
    dataInsight?: string | null
}) {
    return (
        <zaptic-button
            type={props.type === 'warning' ? 'negative' : 'primary'}
            size={props.size || 'auto'}
            onClick={props.onConfirmClicked}
            disabled={props.isSubmitButtonDisabled}
            data-insight={props.dataInsight}
        >
            {props.confirmButtonText || window.i18n('appcore:common.done')}
        </zaptic-button>
    )
}

ConfirmModal.defaultProps = {
    type: 'warning',
}
